import { FC, HTMLAttributes } from "react";
import { Dropdown } from "../Dropdown";

import { ArrowDownV2Icon } from "../../assets/img/icons";
import FieldError from "../FieldError";
import { Menu } from "../Menu";
import { MenuItem } from "../Menu/MenuItem";
import Text from "../Text";
import styles from "./styles.module.scss";

type Placement =
    | "top"
    | "top-start"
    | "top-end"
    | "bottom"
    | "bottom-start"
    | "bottom-end";

type Props = Omit<HTMLAttributes<HTMLDivElement>, "onChange"> & {
    options: Option[];
    type?: "single" | "multiple";
    placeholder?: string;
    value?: string | string[];
    onChange?: (selected: string | string[]) => void;
    classNameTrigger?: string;
    size?: "small" | "medium";
    placement?: Placement;
    error?: string;
};

type Option = {
    label: string;
    value: string;
};

const Select: FC<Props> = ({
    options,
    type = "single",
    placeholder = "-",
    value = [],
    onChange,
    classNameTrigger,
    size = "medium",
    placement = "bottom-start",
    error,
}) => {
    const isMultiple = type === "multiple";

    const selectedLabels = (() => {
        if (Array.isArray(value)) {
            if (value.length === 0) return [];
            return options
                .filter((option) => value.includes(option.value))
                .map((option) => option.label);
        }

        if (typeof value === "string" && value) {
            const found = options.find((option) => option.value === value);
            return found ? [found.label] : [];
        }

        return [];
    })();

    const trigger = (
        <div
            className={`${styles.trigger} ${
                classNameTrigger ? classNameTrigger : ""
            } ${styles[size]}${error ? ` ${styles.triggerError}` : ""}`}
            aria-invalid={Boolean(error)}
        >
            <Text variant="body" className={styles.triggerText}>
                {selectedLabels.length > 0
                    ? selectedLabels.join(", ")
                    : placeholder}
            </Text>
            <ArrowDownV2Icon className={styles.triggerArrow} />
        </div>
    );

    const handleItemClick = (item: string) => {
        if (!isMultiple) {
            const isCurrentlySelected = Array.isArray(value)
                ? value.includes(item)
                : value === item;

            if (isCurrentlySelected) {
                // Désélection en mode single
                onChange?.("");
                return;
            }

            onChange?.(item);
        } else {
            let newValue = Array.isArray(value) ? [...value] : [];
            if (newValue.includes(item)) {
                newValue = newValue.filter((val) => val !== item);
            } else {
                newValue.push(item);
            }
            onChange?.(newValue);
        }
    };

    return (
        <div className={styles.root}>
            <Dropdown trigger={trigger} placement={placement}>
                <Menu className={styles.menu}>
                    {options.map((option) => (
                        <MenuItem
                            checkable
                            isChecked={
                                Array.isArray(value)
                                    ? value.includes(option.value)
                                    : value === option.value
                            }
                            key={option.value}
                            onClick={() => handleItemClick(option.value)}
                        >
                            {option.label}
                        </MenuItem>
                    ))}
                </Menu>
            </Dropdown>
            <FieldError error={error} />
        </div>
    );
};

export default Select;
